import React from 'react';
import {
  Alert,
  Button,
  Field,
  Input,
  Spinner,
  Text,
} from '@billda/design-system';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { RHFPhoneInput } from '../../../shared/components/forms/RHFPhoneInput';
import { useCaptureProfile } from '../hooks/useCaptureProfile';
import {
  profileCaptureSchema,
  ProfileCaptureFormData,
} from '../types/profileCaptureSchema';

interface ProfileCaptureFormProps {
  invitationId: string;
  organisationName: string;
  onSuccess: () => void;
  onError: (message: string) => void;
  onDecline: () => void;
  initialGivenName?: string;
  initialFamilyName?: string;
}

const ProfileCaptureForm: React.FC<ProfileCaptureFormProps> = ({
  invitationId,
  organisationName,
  onSuccess,
  onError,
  onDecline,
  initialGivenName,
  initialFamilyName,
}) => {
  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = useForm({
    defaultValues: {
      givenName: initialGivenName ?? '',
      familyName: initialFamilyName ?? '',
      primaryPhoneNumber: '',
    },
    resolver: yupResolver(profileCaptureSchema) as any,
    mode: 'onChange',
  });

  const { mutate: captureProfile, isPending } = useCaptureProfile();

  const onSubmit = (data: any) => {
    const formData: ProfileCaptureFormData = {
      givenName: data.givenName,
      familyName: data.familyName,
      primaryPhoneNumber: data.primaryPhoneNumber,
    };
    captureProfile(
      { invitationId, data: formData },
      {
        onSuccess: () => {
          onSuccess();
        },
        onError: error => {
          onError(
            error instanceof Error
              ? error.message
              : 'Failed to capture profile information'
          );
        },
      }
    );
  };

  return (
    <div className="w-full">
      <div className="mb-8 text-center">
        <Text
          as="h1"
          variant="h4"
          className="mb-2 font-semibold text-content-primary"
          data-testid="profile-capture-title"
        >
          Complete Your Profile
        </Text>
        <Text as="div" variant="body1" color="secondary">
          Please provide your details to join {organisationName}
        </Text>
      </div>

      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="flex flex-col gap-6">
          <div
            className="flex flex-col gap-4 lg:flex-row"
            data-testid="profile-name-row"
          >
            <div className="flex-1">
              <Controller
                name="givenName"
                control={control}
                render={({ field, fieldState }) => (
                  <Field label="First Name" error={fieldState.error?.message}>
                    <Input
                      {...field}
                      invalid={!!fieldState.error}
                      disabled={isPending}
                      autoComplete="given-name"
                      data-testid="profile-given-name-input"
                    />
                  </Field>
                )}
              />
            </div>

            <div className="flex-1">
              <Controller
                name="familyName"
                control={control}
                render={({ field, fieldState }) => (
                  <Field label="Last Name" error={fieldState.error?.message}>
                    <Input
                      {...field}
                      invalid={!!fieldState.error}
                      disabled={isPending}
                      autoComplete="family-name"
                      data-testid="profile-family-name-input"
                    />
                  </Field>
                )}
              />
            </div>
          </div>

          <RHFPhoneInput
            name="primaryPhoneNumber"
            control={control}
            label="Phone Number"
            fullWidth
            disabled={isPending}
            required
            data-testid="profile-phone-input"
          />

          <Alert
            data-testid="profile-consent-section"
            severity="info"
            className="mb-4"
          >
            We&apos;ll share your name and contact details with{' '}
            {organisationName} so they can add you to their organisation.
          </Alert>

          <div className="flex flex-col justify-end gap-4 sm:flex-row">
            <Button
              variant="outline"
              onClick={onDecline}
              data-testid="profile-consent-decline-button"
            >
              Decline
            </Button>
            <Button
              variant="neutral"
              type="submit"
              disabled={!isValid || isPending}
              data-testid="profile-submit-button"
            >
              {isPending ? (
                <>
                  <Spinner size={20} className="mr-2" />
                  Saving...
                </>
              ) : (
                'Accept and Continue'
              )}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default ProfileCaptureForm;
